import { ThreeElements, useFrame, useThree } from '@react-three/fiber';
import React, { FunctionComponent, memo, useEffect, useLayoutEffect, useRef } from 'react';
import { PMREMGenerator, Scene, SRGBColorSpace, WebGLRenderTarget } from 'three';
import { IContent360Scene } from '../r3f-components/component-data-structure';
import { useGetCurrentSceneId } from '../../hooks/general';
import { useDataStore, useSceneStore } from '../../zustand';
import { hlsVideoCache } from '../r3f-components/hooks';
import { return360VideoIdBySceneId } from '../r3f-components/utils/content360';
import { filestore, FsImage360 } from '@/filestore';
import { getSceneIndexBySceneId } from '@/utils';

let pmremGenerator: PMREMGenerator | undefined;

const ArScene: FunctionComponent<ThreeElements['scene']> = ({ children, ...props }) => {
	const { gl, raycaster } = useThree()
	const arSceneRef = useRef<Scene>(null!);

    const currentSceneId = useGetCurrentSceneId();
    const componentsById = useDataStore(({ componentsById }) => componentsById);

	useLayoutEffect(() => {
		// Set the raycaster to only hit test against objects in scene layer (i.e. scene index)
		const sceneIndex = getSceneIndexBySceneId(currentSceneId, useDataStore.getState().rootId, useDataStore.getState().componentsById)
		if (typeof sceneIndex !== 'undefined') {
			raycaster.layers.set(sceneIndex + 1)
		};
	}, [currentSceneId])
	
	// DO NOT REMOVE: required to trigger a re-render when video is available
	const _isVideoAvailable = useSceneStore(({ videoTextureAvailableById }) => videoTextureAvailableById[currentSceneId]);

	const currentScene = componentsById?.[currentSceneId] as IContent360Scene | undefined;
	const { filestoreId, type: content360Type } = currentScene?.content360 || {};

	if (!pmremGenerator) pmremGenerator = new PMREMGenerator(gl);

	const video = hlsVideoCache[return360VideoIdBySceneId(currentSceneId)]?.[1];
	const videoTexture = hlsVideoCache[return360VideoIdBySceneId(currentSceneId)]?.[0];

	// Set the scene environment to background 360 texture if it exists.
	useEffect(() => {
		if (!video || !videoTexture || !pmremGenerator) return;
		arSceneRef.current.environment = null;
		let renderTexture: WebGLRenderTarget | null = null;
		let videoRequestId: number | null = null;
		videoTexture.colorSpace = SRGBColorSpace;

		const uploadVideoFrame = () => {
			if (!!video.width && !!video.height) {
				const oldXRState = gl.xr.enabled 
				gl.xr.enabled = false;
				renderTexture = pmremGenerator!.fromEquirectangular(videoTexture, renderTexture)
				if (arSceneRef.current) arSceneRef.current.environment = renderTexture.texture;
				gl.xr.enabled = oldXRState;
			}
			videoRequestId = video.requestVideoFrameCallback(uploadVideoFrame)
		}
		
		uploadVideoFrame();

		return () => {
			// Cleanup
			video.cancelVideoFrameCallback(videoRequestId!)
		}
	}, [video, videoTexture])

	useEffect(() => {
		if (!filestoreId || content360Type !== 'image' || typeof pmremGenerator === 'undefined') return;
		const imgFile = filestore.get<FsImage360>(filestoreId)
		imgFile.getTexture().then(imgTexture => {
			if (typeof pmremGenerator === 'undefined' || imgTexture === null || imgTexture.image === null) {
				arSceneRef.current.environment = null;
				return
			};
			imgTexture.name = imgFile.getImageUrl();
			imgTexture.colorSpace = SRGBColorSpace; // Is this correct?

			arSceneRef.current.environment = null;
			const oldXRState = gl.xr.enabled 
			gl.xr.enabled = false;
			arSceneRef.current.environment = pmremGenerator.fromEquirectangular(imgTexture).texture;
			gl.xr.enabled = oldXRState;
		})
	}, [filestoreId, currentSceneId])

	useFrame(({ gl, camera }) => {
		gl.autoClear = true;
		gl.render(arSceneRef.current, camera);
	}, 10);

	return (
		<scene ref={arSceneRef} {...props} name="ArScene">
			{children}
		</scene>
	);
};

ArScene.displayName = 'ArScene';
export default memo(ArScene);
